import {FC, useMemo, useState} from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {Box, IconButton, Tooltip} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';

import {
  OrderItem,
  OrderItemOwnership,
  OrderItemRoleBadge,
  useGetOrderItems,
} from '@entities/order';
import {UserLink} from '@entities/user';
import {
  CurrencyValue,
  MrTable,
  rightAlignmentColumnProps,
} from '@shared/components';
import {DATETIME_FORMAT, entityTableCommonProps} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz} from '@shared/utils';
import {OrderRefLink} from './OrderRefLink';
import {OwnershipDialog} from './OwnershipDialog';

import {PaginationState} from '@/shared/types';

const localStorageKey = 'orderItemTableV3';

interface OwnershipBadgeProps {
  ownership: OrderItemOwnership[];
  onClick: () => void;
}

const OwnershipBadge: FC<OwnershipBadgeProps> = ({ownership, onClick}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    width={24}
    height={24}
    border="1px solid"
    borderColor="divider"
    borderRadius="4px"
    bgcolor="transparent"
    sx={{cursor: 'pointer'}}
  >
    {ownership.length}
  </Box>
);

const CopyIdButton = ({id}: {id: string}) => (
  <Tooltip title={id}>
    <IconButton
      size="small"
      onClick={() => void navigator.clipboard.writeText(id)}
    >
      <ContentCopyIcon sx={{fontSize: 14}} />
    </IconButton>
  </Tooltip>
);

const createColumns = (
  eventId: string,
  onShowOwnership: (ownership: OrderItemOwnership[]) => void,
): MRT_ColumnDef<OrderItem>[] => [
  {
    accessorKey: 'id',
    header: 'ID',
    Cell: ({row}) => (
      <Box display="flex" alignItems="center" gap={0.5}>
        <OrderItemRoleBadge role={row.original.role} />
        <CopyIdButton id={row.original.id} />
      </Box>
    ),
    enableSorting: false,
    size: 130,
  },
  {
    accessorKey: 'created',
    header: 'Created',
    Cell: ({row}) => formatInTz(row.original.created, DATETIME_FORMAT),
    size: 160,
  },
  {
    accessorKey: 'updated',
    header: 'Updated',
    Cell: ({row}) => formatInTz(row.original.updated, DATETIME_FORMAT),
    size: 160,
  },
  {
    accessorKey: 'price',
    header: 'Price',
    // Real and promo together make up the price, so they're shown alongside it
    Cell: ({row}) => {
      const {price, real, promo} = row.original;

      return (
        <>
          <CurrencyValue value={price} /> (<CurrencyValue value={real} />/
          <CurrencyValue value={promo} />)
        </>
      );
    },
    size: 220,
    ...rightAlignmentColumnProps,
  },
  {
    accessorKey: 'userId',
    header: 'User',
    Cell: ({row}) => <UserLink userId={row.original.userId} />,
    size: 130,
  },
  {
    accessorKey: 'counterpartyUserId',
    header: 'Counterparty user',
    Cell: ({row}) => <UserLink userId={row.original.counterpartyUserId} />,
    size: 130,
  },
  {
    accessorKey: 'counterpartyOrderId',
    header: 'Counterparty order',
    Cell: ({row}) => (
      <Box display="flex" alignItems="center" gap={0.5}>
        <OwnershipBadge
          ownership={row.original.ownership}
          onClick={() => onShowOwnership(row.original.ownership)}
        />
        <OrderRefLink
          eventId={eventId}
          orderId={row.original.counterpartyOrderId}
        />
      </Box>
    ),
    size: 200,
  },
];

interface Props {
  orderId: string;
  eventId: string;
}

export const OrderItemsTable: FC<Props> = ({orderId, eventId}) => {
  const [selectedOwnership, setSelectedOwnership] = useState<
    OrderItemOwnership[] | null
  >(null);

  const columns = useMemo(
    () => createColumns(eventId, setSelectedOwnership),
    [eventId],
  );

  const {urlState, setUrl} = useUrlState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const {data, isFetching, error} = useGetOrderItems(
    {
      orderId,
      page: urlState.pageIndex + 1,
      pageSize: urlState.pageSize,
    },
    {enabled: !!orderId},
  );

  return (
    <>
      <MrTable
        columns={columns}
        data={data?.items ?? []}
        error={error}
        enableColumnOrdering
        enableColumnDragging={false}
        localStorageKeyForSettings={localStorageKey}
        enableHiding
        /////////////////////////////
        // pagination + state
        enablePagination
        manualPagination
        onPaginationChange={setUrl}
        rowCount={data?.total ?? 0}
        /////////////////////////////
        state={{
          isLoading: isFetching,
          pagination: urlState,
        }}
        {...entityTableCommonProps}
        enableRowActions={false}
      />

      <OwnershipDialog
        isOpen={selectedOwnership !== null}
        onClose={() => setSelectedOwnership(null)}
        eventId={eventId}
        ownership={selectedOwnership ?? []}
      />
    </>
  );
};
